import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, Plus, Trash2, X } from 'lucide-react';
import {
  Button, Input, Label, NNSelect, ICON_PX,
} from '../ui';

const COMPLETION_OPTIONS = [
  { value: 'checkbox', label: 'Check off' },
  { value: 'checkbox_note', label: 'Add a note' },
  { value: 'checkbox_linked', label: 'Attach evidence' },
];

const DOMAIN_OPTIONS = [
  { value: 'safety', label: 'Safety' },
  { value: 'quality', label: 'Quality' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'staffing', label: 'Staffing' },
  { value: 'access', label: 'Access' },
  { value: 'finance', label: 'Finance' },
  { value: 'operations', label: 'Operations' },
  { value: 'communications', label: 'Communications' },
];

const TYPE_LABELS = {
  task: 'Task Step',
  gate: 'Decision',
  join: 'Wait Point',
  end: 'Finish',
  parallel: 'Parallel Block',
  timer: 'Timer / SLA',
  subprocess: 'Sub-process',
};

/**
 * Right sidebar for configuring a selected node in build mode.
 * Renders different fields per node type.
 */
function NodeConfigPanel({
  selectedNode,
  taskLibrary = [],
  workflows = [],
  swimlanes = [],
  onUpdateNode,
  onDeleteNode,
  onDeselectNode,
}) {
  const [form, setForm] = useState({});
  const [taskSearch, setTaskSearch] = useState('');

  // Reset form when node changes
  useEffect(() => {
    if (selectedNode) {
      setForm({
        label: selectedNode.data?.label || '',
        completionType: selectedNode.data?.completionType || 'checkbox',
        estimatedMinutes: selectedNode.data?.estimatedMinutes || '',
        domain: selectedNode.data?.domain || '',
        taskId: selectedNode.data?.taskId || null,
        options: selectedNode.data?.options || ['Yes', 'No'],
        swimlane: selectedNode.data?.swimlane || '',
        duration: selectedNode.data?.duration || '',
        linkedWorkflowId: selectedNode.data?.linkedWorkflowId || '',
      });
      setTaskSearch('');
    }
  }, [selectedNode]);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    onUpdateNode?.(selectedNode.id, { [field]: value });
  }, [selectedNode, onUpdateNode]);

  const handleAddOption = useCallback(() => {
    const next = [...(form.options || []), `Option ${(form.options?.length || 0) + 1}`];
    updateField('options', next);
  }, [form.options, updateField]);

  const handleRemoveOption = useCallback((idx) => {
    if ((form.options?.length || 0) <= 2) return;
    const next = form.options.filter((_, i) => i !== idx);
    updateField('options', next);
  }, [form.options, updateField]);

  const handleOptionChange = useCallback((idx, value) => {
    const next = form.options.map((opt, i) => (i === idx ? value : opt));
    updateField('options', next);
  }, [form.options, updateField]);

  const filteredTasks = useMemo(() => {
    if (!taskSearch) return taskLibrary.slice(0, 15);
    const q = taskSearch.toLowerCase();
    return taskLibrary
      .filter((t) => t.name.toLowerCase().includes(q) || t.domain.toLowerCase().includes(q))
      .slice(0, 15);
  }, [taskLibrary, taskSearch]);

  // Swimlane options for dropdown
  const swimlaneOptions = useMemo(() => [
    { value: '', label: 'No swimlane' },
    ...swimlanes.map((sl) => ({ value: sl.id, label: `${sl.label} (${sl.role})` })),
  ], [swimlanes]);

  if (!selectedNode) return null;

  const nodeType = selectedNode.type;
  const typeLabel = TYPE_LABELS[nodeType] || 'Step';

  return (
    <div className="flex flex-col h-full border-l border-slate-200 bg-white rounded-r-xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
        <div className="flex flex-col min-w-0">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{typeLabel}</span>
          <span className="text-sm font-semibold text-text-dark truncate">{form.label || 'Untitled'}</span>
        </div>
        <button type="button" onClick={onDeselectNode} className="text-slate-400 hover:text-text-dark rounded-lg p-1 hover:bg-slate-50">
          <X size={ICON_PX.md} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Label — all node types */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-slate-600">Name</Label>
          <Input
            value={form.label}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="What needs to happen?"
          />
        </div>

        {/* Swimlane assignment — all node types except swimlane itself */}
        {nodeType !== 'swimlane' && swimlanes.length > 0 && (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">Swimlane (role)</Label>
            <NNSelect
              value={form.swimlane}
              onChange={(v) => updateField('swimlane', v)}
              options={swimlaneOptions}
              placeholder="Assign to a role"
            />
          </div>
        )}

        {/* Task-specific fields */}
        {nodeType === 'task' && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">How is this completed?</Label>
              <NNSelect
                value={form.completionType}
                onChange={(v) => updateField('completionType', v)}
                options={COMPLETION_OPTIONS}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Estimated minutes</Label>
              <Input
                type="number"
                min={1}
                value={form.estimatedMinutes}
                onChange={(e) => updateField('estimatedMinutes', e.target.value ? Number(e.target.value) : '')}
                placeholder="e.g., 5"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Category</Label>
              <NNSelect
                value={form.domain}
                onChange={(v) => updateField('domain', v)}
                options={DOMAIN_OPTIONS}
                placeholder="Choose a category"
              />
            </div>

            {/* Task library link */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Link to existing task (optional)</Label>
              <div className="relative">
                <Search size={ICON_PX.sm} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={taskSearch}
                  onChange={(e) => setTaskSearch(e.target.value)}
                  placeholder="Search task library..."
                  className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-2 text-sm text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/40"
                />
              </div>
              {taskSearch && (
                <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-200 bg-white">
                  {filteredTasks.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-400">No matching tasks</div>
                  ) : (
                    filteredTasks.map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => {
                          updateField('taskId', task.id);
                          updateField('label', task.name);
                          updateField('domain', task.domain);
                          setTaskSearch('');
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                      >
                        <div className="text-sm text-text-dark truncate">{task.name}</div>
                        <div className="text-xs text-slate-400">{task.domain} · {task.cadence}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
              {form.taskId && (
                <div className="text-xs text-aqua">Linked to task: {form.taskId}</div>
              )}
            </div>
          </>
        )}

        {/* Gate-specific fields */}
        {nodeType === 'gate' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-slate-600">Paths</Label>
              <Button variant="ghost" size="sm" onClick={handleAddOption}>
                <Plus size={ICON_PX.sm} className="mr-1" /> Add path
              </Button>
            </div>
            <div className="space-y-1.5">
              {(form.options || []).map((opt, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <Input
                    value={opt}
                    onChange={(e) => handleOptionChange(idx, e.target.value)}
                    placeholder={`Path ${idx + 1}`}
                  />
                  {(form.options?.length || 0) > 2 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(idx)}
                      className="p-1.5 text-slate-400 hover:text-red-critical hover:bg-red-bg rounded"
                      title="Remove path"
                    >
                      <Trash2 size={ICON_PX.sm} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">
              The decision creates one branch per path. Practice managers will choose which path to follow at runtime.
            </p>
          </div>
        )}

        {/* Parallel-specific fields */}
        {nodeType === 'parallel' && (
          <div className="space-y-2">
            <p className="text-xs text-slate-400">
              A parallel block runs multiple tracks at the same time. All tracks must
              complete before the workflow continues. Connect child steps inside this block.
            </p>
          </div>
        )}

        {/* Timer-specific fields */}
        {nodeType === 'timer' && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Duration (hours)</Label>
              <Input
                type="number"
                min={1}
                value={form.duration ? Math.floor(form.duration / 3600000) : ''}
                onChange={(e) => updateField('duration', e.target.value ? Number(e.target.value) * 3600000 : '')}
                placeholder="e.g., 48"
              />
              <p className="text-xs text-slate-400">
                How long before this timer fires. Used for SLA deadlines — if the
                previous step isn't done in time, the workflow escalates.
              </p>
            </div>
          </div>
        )}

        {/* Subprocess-specific fields */}
        {nodeType === 'subprocess' && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Linked workflow</Label>
              <NNSelect
                value={form.linkedWorkflowId}
                onChange={(v) => updateField('linkedWorkflowId', v)}
                options={[
                  { value: '', label: 'None' },
                  ...workflows.map((wf) => ({ value: wf.id, label: wf.name })),
                ]}
                placeholder="Choose a workflow to nest"
              />
            </div>
            <p className="text-xs text-slate-400">
              A sub-process runs another workflow as a step. When the nested
              workflow completes, this step is marked as done.
            </p>
          </div>
        )}

        {/* Join/End — only label */}
        {(nodeType === 'join' || nodeType === 'end') && (
          <p className="text-xs text-slate-400">
            {nodeType === 'join'
              ? 'A wait point pauses the workflow until all incoming steps are complete.'
              : 'The finish line — marks the workflow as done.'}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-3 py-3 border-t border-slate-100 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => onDeleteNode?.(selectedNode.id)}>
          <Trash2 size={ICON_PX.sm} className="mr-1 text-red-critical" />
          <span className="text-red-critical">Delete step</span>
        </Button>
      </div>
    </div>
  );
}

export default NodeConfigPanel;
